import { CaretRight, Notepad, Receipt } from "@/components/shared/icons";
import { Card, CardContent, Skeleton, View } from "@/components/ui";
import { IconButton } from "@/components/ui/icon-button";
import { copyClipboard } from "@/lib/helpers/copy-clipboard";
import { extractTokenFromUrl, GST_REGISTRATION } from "@/lib/helpers/string";
import { ListItem } from "./list-item";
import { PaymentStatusBadge } from "@/components/shared/invoices/invoice-card/payment-status-badge";
import { formatDate } from "@/lib/helpers/date";
import { ListHeading } from "@/components/shared/list-header";
import { LineItemList } from "@/components/shared/line-item/line-item-list";
import { formatPrice, SINGAPORE_TAX } from "@/lib/helpers/number";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { Typography } from "@/components/shared/typography";
import { Link } from "@/components/shared/link";
import { Divider } from "@/components/shared/divider";
import { InvoicePaymentStatusEnum } from "@/__generated__/graphql";
import type { InvoiceDetailData } from "@/gqlhooks/invoice/useInvoiceByIdQuery";

type InvoiceDetailCardProps = {
  invoice: InvoiceDetailData;
  loading: boolean;
};

export const InvoiceDetailCard = ({
  invoice,
  loading,
}: InvoiceDetailCardProps) => {
  const { isMobile } = useWindowDimensions();

  return (
    <Card border={isMobile ? "none" : "default"} className="mb-[144px]">
      <CardContent className="flex flex-col gap-2">
        <View className="flex flex-row justify-between gap-2">
          <View className="flex flex-row items-center gap-1">
            <Typography variant="body-md">Invoice #{invoice.id}</Typography>
            <IconButton
              variant="ghost"
              color="foreground"
              size="sm"
              className="p-0"
              iconName="copy"
              onClick={() => copyClipboard(`Invoice #${invoice.id}`)}
            />
          </View>
          {loading ? (
            <Skeleton className="h-5 w-[100px]" />
          ) : (
            <Link
              href={
                invoice.pdfUrl
                  ? `/profile/invoices/${invoice.id}/view-invoice-pdf?token=${extractTokenFromUrl(invoice.pdfUrl)}`
                  : "#"
              }
            >
              <View className="flex flex-row items-center gap-1">
                <Typography variant="body-md" color="brand-primary">
                  View PDF
                </Typography>
                <CaretRight className="size-4 text-brand-primary" />
              </View>
            </Link>
          )}
        </View>
        <Divider />
        {invoice.paymentStatus === InvoicePaymentStatusEnum.Paid && (
          <>
            <View className="flex flex-row justify-between gap-2">
              <View className="flex flex-row items-center gap-1">
                <Typography variant="body-md">Receipt #{invoice.id}</Typography>
                <IconButton
                  variant="ghost"
                  color="foreground"
                  className="p-0"
                  size="sm"
                  iconName="copy"
                  onClick={() => copyClipboard(`Receipt #${invoice.id}`)}
                />
              </View>
              {loading ? (
                <Skeleton className="h-5 w-[100px]" />
              ) : (
                <Link
                  href={
                    invoice.pdfReceiptUrl
                      ? `/profile/invoices/${invoice.id}/view-receipt-pdf?token=${extractTokenFromUrl(invoice.pdfReceiptUrl)}`
                      : "#"
                  }
                >
                  <View className="flex flex-row items-center gap-1">
                    <Typography variant="body-md" color="brand-primary">
                      View PDF
                    </Typography>
                    <CaretRight className="size-4 text-brand-primary" />
                  </View>
                </Link>
              )}
            </View>
            <Divider />
          </>
        )}
        <ListItem label="Status">
          {loading ? (
            <Skeleton className="h-5 w-[100px]" />
          ) : (
            <PaymentStatusBadge
              balance={invoice.balance}
              amount={invoice.amount}
              dueDate={invoice.dueDate}
              status={invoice.paymentStatus}
            />
          )}
        </ListItem>
        <Divider />
        <ListItem label="Invoice Date">
          {loading ? (
            <Skeleton className="h-4 w-[200px]" />
          ) : (
            <Typography variant="body-md">
              {formatDate(invoice.issueDate, "dd MMM yyyy")}
            </Typography>
          )}
        </ListItem>
        <Divider />
        <ListItem label="GST Registration No">
          <Typography variant="body-md">{GST_REGISTRATION}</Typography>
        </ListItem>
        <Divider />
        <ListItem label="Due Date">
          {loading ? (
            <Skeleton className="h-4 w-[200px]" />
          ) : (
            <Typography variant="body-md">
              {formatDate(invoice.dueDate, "dd MMM yyyy")}
            </Typography>
          )}
        </ListItem>
      </CardContent>
      <Divider />
      <CardContent className="flex flex-col gap-2">
        <View className="flex flex-col gap-1">
          <View className="py-2">
            <ListHeading icon={Notepad} title="Items" />
          </View>
          <LineItemList
            lineItems={invoice.lineItems}
            withPrice
            mergedLineItems={false}
          />
        </View>
        <Divider className="py-2" />
        <View className="flex flex-col gap-1">
          <View className="py-2">
            <ListHeading icon={Receipt} title="Payment Summary" />
          </View>
          <ListItem label="Subtotal">
            {loading ? (
              <Skeleton className="h-4 w-[200px]" />
            ) : (
              <Typography variant="body-md">
                {formatPrice(invoice.lineItemsTotal)}
              </Typography>
            )}
          </ListItem>
          {!!invoice.appliedVoucher && (
            <ListItem
              label={`Discount Voucher ${invoice.discountAmount > 0 ? `(${invoice.appliedVoucher.discountValue}%)` : ""}`}
            >
              <Typography variant="body-md">
                -{formatPrice(invoice.discountAmount)}
              </Typography>
            </ListItem>
          )}
          {!!invoice.amount && (
            <ListItem label={`GST (${SINGAPORE_TAX * 100}%)`}>
              {loading ? (
                <Skeleton className="h-4 w-[200px]" />
              ) : (
                <Typography variant="body-md">
                  {formatPrice(invoice.amount)}
                </Typography>
              )}
            </ListItem>
          )}

          {!!invoice.paidAmount && (
            <ListItem label="Payment confirmed">
              {loading ? (
                <Skeleton className="h-4 w-[200px]" />
              ) : (
                <Typography variant="body-md">
                  -{formatPrice(invoice.paidAmount)}
                </Typography>
              )}
            </ListItem>
          )}
        </View>
        <Divider className="py-2" />
        <View className="flex flex-row items-end justify-between gap-2">
          <Typography variant="body-md">Total Amount</Typography>
          {loading ? (
            <Skeleton className="h-4 w-[200px]" />
          ) : (
            <View className="flex flex-row items-end gap-1">
              <Typography variant="body-md" color="brand-primary">
                {formatPrice(invoice.balance)}
              </Typography>
              {!!invoice.amount && (
                <Typography
                  as="span"
                  variant="body-md"
                  color="foreground-intermediate"
                >
                  incl. GST
                </Typography>
              )}
            </View>
          )}
        </View>
      </CardContent>
    </Card>
  );
};
